import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type UserPalette,
  useCreatePalette,
  useUpdatePalette,
  type WorkspacePaletteRights,
} from '@/entities/palette';
import { FormError } from '@/shared/forms';
import { isHexColor } from '@/shared/lib/color';
import { Button, Combobox, Dialog, IconButton, Icons, TextField } from '@/shared/ui';

import styles from './ColorPalettes.module.css';
import { PaletteStrip } from './PaletteStrip';

export const MAX_COLORS = 100;
const NAME_MAX = 100;
const NEW_COLOR = '#4c8dff';

export interface EditorSeed {
  /** Present: an existing palette is edited; absent: a new one is made (from scratch or a copy). */
  palette?: UserPalette;
  name: string;
  colors: readonly string[];
  /** Where a new palette goes by default (`null` = personal). */
  workspaceId?: string | null;
}

const normalize = (draft: string): string | null => {
  const text = draft.trim().toLowerCase();
  const hex = text.startsWith('#') ? text : `#${text}`;
  return isHexColor(hex) ? hex : null;
};

/**
 * One editor for personal and shared palettes: name, an ordered list of colors (the browser's own
 * color editor + a HEX field each, move earlier / later, remove, add), a preview and an explicit
 * Save. New palettes choose where they live (personal, or a workspace where the viewer may create
 * palettes); an existing one keeps its place and its creator.
 */
export function PaletteEditor({
  seed,
  workspaces,
  onClose,
  onSaved,
}: {
  seed: EditorSeed;
  /** Workspaces where the viewer may create a shared palette. */
  workspaces: readonly WorkspacePaletteRights[];
  onClose: () => void;
  onSaved?: (palette: UserPalette) => void;
}) {
  const { t } = useTranslation();
  const create = useCreatePalette();
  const update = useUpdatePalette();
  const [name, setName] = useState(seed.name);
  const [drafts, setDrafts] = useState<string[]>([...seed.colors]);
  const [target, setTarget] = useState<string>(seed.workspaceId ?? '');
  const [submitted, setSubmitted] = useState(false);
  const editing = seed.palette !== undefined;
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const parsed = drafts.map(normalize);
  const valid = name.trim() !== '' && parsed.length > 0 && parsed.every((color) => color !== null);
  const set = (index: number, value: string) => {
    setDrafts((current) => current.map((color, i) => (i === index ? value : color)));
  };
  const move = (index: number, delta: -1 | 1) => {
    setDrafts((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      if (item !== undefined) next.splice(index + delta, 0, item);
      return next;
    });
  };
  const save = () => {
    setSubmitted(true);
    if (!valid) return;
    const colors = parsed.flatMap((color) => (color ? [color] : []));
    const done = (palette: UserPalette) => {
      onSaved?.(palette);
      onClose();
    };
    if (seed.palette) {
      update.mutate(
        { id: seed.palette.id, name: name.trim(), colors, expected_version: seed.palette.version },
        { onSuccess: done },
      );
    } else {
      create.mutate(
        { workspaceId: target || null, name: name.trim(), colors },
        { onSuccess: done },
      );
    }
  };
  const targetOptions = [
    { value: '', label: t('palettes.targetPersonal') },
    ...workspaces
      .filter((w) => w.can_create)
      .map((w) => ({
        value: w.workspace_id,
        label: t('palettes.targetWorkspace', { name: w.workspace_name }),
      })),
  ];

  return (
    <Dialog title={t(editing ? 'palettes.edit' : 'palettes.create')} onClose={onClose}>
      <form
        className={styles.editor}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <TextField
          label={t('palettes.name')}
          value={name}
          maxLength={NAME_MAX}
          autoFocus
          error={submitted && name.trim() === '' ? t('palettes.nameRequired') : undefined}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        {!editing && targetOptions.length > 1 && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t('palettes.target')}</span>
            <Combobox
              label={t('palettes.target')}
              value={target}
              options={targetOptions}
              onChange={setTarget}
            />
          </div>
        )}
        <fieldset className={styles.colorsField}>
          <legend>{t('palettes.colors')}</legend>
          {drafts.length === 0 && <p className={styles.muted}>{t('palettes.noColors')}</p>}
          <ol className={styles.colorRows}>
            {drafts.map((draft, index) => {
              const color = parsed[index] ?? null;
              const bad = submitted && color === null;
              return (
                <li key={index} className={styles.colorRow}>
                  <label
                    className={styles.nativeColor}
                    style={{ background: color ?? 'transparent' }}
                  >
                    <span className="visually-hidden">
                      {t('palettes.colorField', { index: index + 1 })}
                    </span>
                    <input
                      type="color"
                      value={color ?? NEW_COLOR}
                      onChange={(event) => {
                        set(index, event.target.value);
                      }}
                    />
                  </label>
                  <input
                    type="text"
                    className={styles.hexInput}
                    value={draft}
                    maxLength={7}
                    spellCheck={false}
                    autoComplete="off"
                    placeholder="#rrggbb"
                    aria-label={t('palettes.colorField', { index: index + 1 })}
                    aria-invalid={bad || undefined}
                    onChange={(event) => {
                      set(index, event.target.value);
                    }}
                  />
                  <span className={styles.rowActions}>
                    <IconButton
                      label={t('palettes.moveColorLeft', { color: color ?? draft })}
                      icon={<Icons.arrowUp />}
                      disabled={index === 0}
                      onClick={() => {
                        move(index, -1);
                      }}
                    />
                    <IconButton
                      label={t('palettes.moveColorRight', { color: color ?? draft })}
                      icon={<Icons.arrowDown />}
                      disabled={index === drafts.length - 1}
                      onClick={() => {
                        move(index, 1);
                      }}
                    />
                    <IconButton
                      label={t('palettes.removeColor', { color: color ?? draft })}
                      icon={<Icons.close />}
                      onClick={() => {
                        setDrafts((current) => current.filter((_, i) => i !== index));
                      }}
                    />
                  </span>
                  {bad && (
                    <span role="alert" className={styles.invalid}>
                      {t('palettes.colorInvalid')}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
          <Button
            type="button"
            size="sm"
            disabled={drafts.length >= MAX_COLORS}
            onClick={() => {
              setDrafts((current) => [
                ...current,
                [...parsed].reverse().find((color) => color !== null) ?? NEW_COLOR,
              ]);
            }}
          >
            {t('palettes.addColor')}
          </Button>
          {drafts.length >= MAX_COLORS && (
            <p className={styles.muted}>{t('palettes.tooMany', { max: MAX_COLORS })}</p>
          )}
        </fieldset>
        <div className={styles.previewBlock}>
          <span className={styles.fieldLabel}>{t('palettes.preview')}</span>
          <PaletteStrip
            colors={parsed.filter((color): color is string => color !== null)}
            label={t('palettes.preview')}
          />
        </div>
        <FormError error={error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={pending}>
            {t('palettes.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
